/**
 * The Sconce's depth (2026-10-13).
 *
 * User: "the hub lobby doesn't feel full and voluminous ... the cards/tabs feel
 * flat and don't pull you into the game's lore. Volume via Blender, animations
 * written in code."
 *
 * Blender renders each station as four pixel-aligned layers - back (opaque
 * room), mid (subject), front (near framing) and glow (candle hotspots) - and
 * the hall behind the hub the same way. This file stacks them and moves them:
 *
 *   - a station tilts toward the pointer and its layers slide by different
 *     amounts (parallax), a sheen follows the pointer, and a press sinks the
 *     card in depth;
 *   - the hall behind the hub (and the band art, and a panel's banner) drifts
 *     with the pointer, and on its own very slowly, so a phone - which has no
 *     pointer - is still alive;
 *   - the glow layer flickers (CSS, in app.css).
 *
 * Everything is driven by CSS custom properties from ONE shared pointer
 * listener and ONE rAF loop; the loop only writes a variable when it moved.
 * The motion itself is transform/opacity in app.css. Missing layers are not an
 * error: the station keeps its single old tile image, the hall keeps the
 * painted backdrop, and no broken-image icon is ever shown, because a layer is
 * only inserted once it has decoded.
 *
 * No game state here: this is paint.
 */

export type LayerName = 'back' | 'mid' | 'front' | 'glow';
export const LAYER_ORDER: readonly LayerName[] = ['back', 'mid', 'front', 'glow'];
export const LAYERS_DIR = '/assets/ui/lobby/layers/';
/** The id the hall backdrop's files use (`hall-back.webp` ...). */
export const HALL_ID = 'hall';

/** Dock button `data-tile` -> file prefix. The dock says `rankings`, the art says `standings`. */
const TILE_TO_LAYER_ID: Readonly<Record<string, string>> = {
  quests: 'quests',
  contracts: 'contracts',
  insurance: 'insurance',
  commissions: 'commissions',
  shop: 'shop',
  rankings: 'standings',
};

/** Today's single images, kept as the fallback. */
const TILE_FALLBACK: Readonly<Record<string, string>> = {
  quests: '/assets/ui/lobby/tile-quests.webp',
  contracts: '/assets/ui/lobby/tile-contracts.webp',
  insurance: '/assets/ui/lobby/tile-insurance.webp',
  commissions: '/assets/ui/lobby/tile-commissions.webp',
  shop: '/assets/ui/lobby/tile-vendor.webp',
  rankings: '/assets/ui/lobby/tile-arena.webp',
};

export function layerUrl(id: string, layer: LayerName): string {
  return `${LAYERS_DIR}${id}-${layer}.webp`;
}

/** `data-tile` / panel name -> layer file prefix, or null when it has no station. */
export function stationLayerId(tile: string): string | null {
  return TILE_TO_LAYER_ID[tile] ?? null;
}

export function stationFallbackUrl(tile: string): string | null {
  return TILE_FALLBACK[tile] ?? null;
}

// ─── pure maths (tested) ─────────────────────────────────────────────

export interface UnitPoint { x: number; y: number }

/** Pointer position inside a rect as -1..1 on both axes (centre = 0). Writes into `out`: no allocation. */
export function pointerToUnit(
  rect: { left: number; top: number; width: number; height: number },
  clientX: number,
  clientY: number,
  out: UnitPoint,
): UnitPoint {
  const w = rect.width || 1;
  const h = rect.height || 1;
  out.x = clamp(((clientX - rect.left) / w) * 2 - 1, -1, 1);
  out.y = clamp(((clientY - rect.top) / h) * 2 - 1, -1, 1);
  return out;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Frame-rate independent ease toward a target; `tau` is the time constant in ms. */
export function approach(cur: number, target: number, dtMs: number, tauMs: number): number {
  return cur + (target - cur) * (1 - Math.exp(-dtMs / tauMs));
}

/** A slow, gentle wander in -1..1 for devices with no pointer. `phase` de-syncs the stations. */
export function idleX(nowMs: number, phase: number): number {
  return Math.sin(nowMs * 0.00042 + phase) * 0.5;
}
export function idleY(nowMs: number, phase: number): number {
  return Math.cos(nowMs * 0.00035 + phase * 1.7) * 0.36;
}

// ─── environment ─────────────────────────────────────────────────────

export interface DepthEnv {
  reduced: boolean;
  saveData: boolean;
  /** No hover-capable pointer: idle drift stands in for it. */
  coarse: boolean;
}

export function readDepthEnv(): DepthEnv {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    // No media queries (jsdom, ancient browsers): stay still rather than guess.
    return { reduced: true, saveData: true, coarse: false };
  }
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return {
    reduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    saveData: conn?.saveData === true,
    coarse: window.matchMedia('(hover: none)').matches,
  };
}

// ─── layer stacks ────────────────────────────────────────────────────

export type StackState = 'pending' | 'fallback' | 'layered' | 'none';

export interface LayerStackOptions {
  /** The old single image, shown until (and unless) the layers load. */
  fallbackUrl?: string | null;
  /** Test seam. */
  makeImage?: () => HTMLImageElement;
}

function setState(stack: HTMLElement, state: StackState): void {
  stack.dataset.state = state;
  stack.dispatchEvent(new CustomEvent('depthstate', { detail: state }));
}

function insertInOrder(stack: HTMLElement, img: HTMLImageElement, layer: LayerName): void {
  const mine = LAYER_ORDER.indexOf(layer);
  for (const child of Array.from(stack.children) as HTMLElement[]) {
    const other = LAYER_ORDER.indexOf(child.dataset.layer as LayerName);
    if (other > mine) { stack.insertBefore(img, child); return; }
  }
  stack.appendChild(img);
}

/**
 * A `.depth-stack` for `id`. The fallback image (if any) is in at once; the
 * back layer is probed first and the others are only attached when it loaded,
 * since mid/front/glow are aligned to it and mean nothing over a different
 * picture. `data-state` and a `depthstate` event report where it got to.
 */
export function buildLayerStack(id: string, opts: LayerStackOptions = {}): HTMLElement {
  const make = opts.makeImage ?? (() => new Image());
  const stack = document.createElement('span');
  stack.className = 'depth-stack';
  stack.setAttribute('aria-hidden', 'true');
  stack.dataset.state = 'pending';

  if (opts.fallbackUrl) {
    const fb = make();
    fb.className = 'depth-fallback';
    fb.alt = '';
    fb.draggable = false;
    fb.decoding = 'async';
    fb.onerror = () => { fb.remove(); };
    fb.src = opts.fallbackUrl;
    stack.appendChild(fb);
    stack.dataset.state = 'fallback';
  } else {
    stack.dataset.state = 'none';
  }

  const load = (layer: LayerName, onDone?: (ok: boolean) => void): void => {
    const img = make();
    img.className = `depth-l depth-l-${layer}`;
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    img.dataset.layer = layer;
    img.onload = () => { insertInOrder(stack, img, layer); onDone?.(true); };
    img.onerror = () => { onDone?.(false); };
    img.src = layerUrl(id, layer);
  };

  load('back', (ok) => {
    if (!ok) return;
    setState(stack, 'layered');
    load('mid');
    load('front');
    load('glow');
  });
  return stack;
}

// ─── the controller ──────────────────────────────────────────────────

interface StationState {
  el: HTMLElement;
  phase: number;
  tx: number; ty: number; ttx: number; tty: number;
  hov: number; thov: number;
  press: number; tpress: number;
  sx: number; sy: number;
  // last written values, to skip redundant style writes
  wtx: number; wty: number; whov: number; wpress: number;
  rect: { left: number; top: number; width: number; height: number } | null;
}

export interface HubDepth {
  /** Panel open: stations stop wandering (they are behind a scrim). */
  setPanelOpen(open: boolean): void;
  dispose(): void;
}

const TAU_TILT = 140;
const TAU_HALL = 380;
const EPS = 0.002;

function r3(v: number): string { return (Math.round(v * 1000) / 1000).toString(); }

/**
 * Wires `root`: upgrades every `.station` with layers and tilt, and drives
 * `--gx/--gy` (-1..1) on `root` for the hall, the band art and the panel
 * banner, which all read it. Returns a disposer.
 */
export function mountHubDepth(root: HTMLElement, hall: HTMLElement | null, envIn?: DepthEnv): HubDepth {
  const env = envIn ?? readDepthEnv();
  const stations: StationState[] = [];
  const unit: UnitPoint = { x: 0, y: 0 };

  root.querySelectorAll<HTMLElement>('.station[data-tile]').forEach((el, i) => {
    const tile = el.dataset.tile ?? '';
    const id = stationLayerId(tile);
    const disc = el.querySelector<HTMLElement>('.station-disc');
    if (id && disc) {
      const stack = buildLayerStack(id, {});
      stack.addEventListener('depthstate', () => { el.dataset.layered = stack.dataset.state === 'layered' ? '1' : ''; });
      disc.appendChild(stack);
    }
    stations.push({
      el, phase: i * 1.3 + 0.4,
      tx: 0, ty: 0, ttx: 0, tty: 0, hov: 0, thov: 0, press: 0, tpress: 0, sx: 50, sy: 40,
      wtx: 0, wty: 0, whov: 0, wpress: 0, rect: null,
    });
  });

  if (hall) {
    const stack = buildLayerStack(HALL_ID, {});
    stack.classList.add('hall-stack');
    stack.addEventListener('depthstate', () => { hall.dataset.layered = stack.dataset.state === 'layered' ? '1' : ''; });
    hall.appendChild(stack);
  }

  let panelOpen = false;
  let raf = 0;
  let last = 0;
  let disposed = false;

  // shared pointer state
  let hoverState: StationState | null = null;
  let pressState: StationState | null = null;
  let px = 0; let py = 0; let havePointer = false;
  let gtx = 0; let gty = 0; let gx = 0; let gy = 0; let wgx = 0; let wgy = 0;
  let rectsDirty = true;

  function stateFor(el: Element | null): StationState | null {
    if (!el) return null;
    const btn = (el as HTMLElement).closest?.('.station');
    if (!btn) return null;
    for (let i = 0; i < stations.length; i++) if (stations[i].el === btn) return stations[i];
    return null;
  }

  function measure(s: StationState): void {
    const r = s.el.getBoundingClientRect();
    s.rect = { left: r.left, top: r.top, width: r.width, height: r.height };
  }

  function aim(s: StationState, clientX: number, clientY: number): void {
    if (rectsDirty || !s.rect) { measure(s); }
    pointerToUnit(s.rect as { left: number; top: number; width: number; height: number }, clientX, clientY, unit);
    s.ttx = unit.x;
    s.tty = unit.y;
    s.sx = (unit.x * 0.5 + 0.5) * 100;
    s.sy = (unit.y * 0.5 + 0.5) * 100;
  }

  function release(s: StationState | null): void {
    if (!s) return;
    s.thov = 0; s.tpress = 0; s.ttx = 0; s.tty = 0; s.rect = null;
  }

  function wake(): void {
    if (raf || disposed || env.reduced || document.hidden) return;
    last = 0;
    raf = requestAnimationFrame(frame);
  }

  const onMove = (e: PointerEvent): void => {
    if (env.reduced) return;
    px = e.clientX; py = e.clientY; havePointer = true;
    if (e.pointerType === 'touch') {
      if (pressState) aim(pressState, e.clientX, e.clientY);
      wake();
      return;
    }
    const s = stateFor(e.target as Element | null);
    if (s !== hoverState) {
      if (hoverState && hoverState !== pressState) release(hoverState);
      hoverState = s;
      rectsDirty = true;
      if (s) { s.rect = null; }
    }
    if (s) { aim(s, e.clientX, e.clientY); rectsDirty = false; s.thov = 1; }
    wake();
  };

  const onDown = (e: PointerEvent): void => {
    if (env.reduced) return;
    const s = stateFor(e.target as Element | null);
    if (!s) return;
    pressState = s;
    s.rect = null;
    aim(s, e.clientX, e.clientY);
    s.thov = 1; s.tpress = 1;
    wake();
  };

  const onUp = (e: PointerEvent): void => {
    if (!pressState) return;
    pressState.tpress = 0;
    if (e.pointerType === 'touch') release(pressState);
    pressState = null;
    wake();
  };

  const onLeaveWindow = (e: PointerEvent): void => {
    if (e.relatedTarget) return;
    havePointer = false;
    if (hoverState) { release(hoverState); hoverState = null; }
    wake();
  };

  const invalidate = (): void => { rectsDirty = true; for (const s of stations) s.rect = null; };

  const onVisibility = (): void => {
    if (document.hidden) { if (raf) { cancelAnimationFrame(raf); raf = 0; } }
    else wake();
  };

  function frame(now: number): void {
    raf = 0;
    if (disposed) return;
    const dt = last ? Math.min(now - last, 50) : 16;
    last = now;

    // Hall / band / banner: pointer (mouse) plus a very slow wander.
    const idle = !env.saveData;
    gtx = (havePointer && !env.coarse ? (px / (window.innerWidth || 1)) * 2 - 1 : 0) + (idle ? idleX(now, 0) * 0.5 : 0);
    gty = (havePointer && !env.coarse ? (py / (window.innerHeight || 1)) * 2 - 1 : 0) + (idle ? idleY(now, 0) * 0.5 : 0);
    gx = approach(gx, gtx, dt, TAU_HALL);
    gy = approach(gy, gty, dt, TAU_HALL);
    if (Math.abs(gx - wgx) > EPS || Math.abs(gy - wgy) > EPS) {
      wgx = gx; wgy = gy;
      root.style.setProperty('--gx', r3(gx));
      root.style.setProperty('--gy', r3(gy));
    }

    let busy = idle; // the wander never settles; a save-data client lets the loop rest
    for (let i = 0; i < stations.length; i++) {
      const s = stations[i];
      const driven = s === hoverState || s === pressState;
      if (env.coarse && !driven && !panelOpen && idle) {
        s.ttx = idleX(now, s.phase);
        s.tty = idleY(now, s.phase);
        s.sx = (s.ttx + 1) * 50; s.sy = (s.tty + 1) * 50;
      }
      s.tx = approach(s.tx, s.ttx, dt, TAU_TILT);
      s.ty = approach(s.ty, s.tty, dt, TAU_TILT);
      s.hov = approach(s.hov, s.thov, dt, TAU_TILT);
      s.press = approach(s.press, s.tpress, dt, 70);
      if (Math.abs(s.tx - s.wtx) > EPS || Math.abs(s.ty - s.wty) > EPS) {
        s.wtx = s.tx; s.wty = s.ty;
        s.el.style.setProperty('--tx', r3(s.tx));
        s.el.style.setProperty('--ty', r3(s.ty));
        s.el.style.setProperty('--sx', `${Math.round(s.sx)}%`);
        s.el.style.setProperty('--sy', `${Math.round(s.sy)}%`);
      }
      if (Math.abs(s.hov - s.whov) > EPS || Math.abs(s.press - s.wpress) > EPS) {
        s.whov = s.hov; s.wpress = s.press;
        s.el.style.setProperty('--hov', r3(s.hov));
        s.el.style.setProperty('--press', r3(s.press));
      }
      const live = driven || s.hov > 0.01 || s.press > 0.01 || Math.abs(s.tx - s.ttx) > 0.01 || Math.abs(s.ty - s.tty) > 0.01;
      if (live) {
        busy = true;
        if (s.el.dataset.live !== '1') s.el.dataset.live = '1';
      } else if (s.el.dataset.live === '1') {
        delete s.el.dataset.live;
      }
    }

    // Idle: the pointer is still and nothing is wandering. One more frame to
    // settle, then the loop rests until the next event.
    if (busy || Math.abs(gx - gtx) > EPS || Math.abs(gy - gty) > EPS) {
      raf = requestAnimationFrame(frame);
    }
  }

  document.addEventListener('pointermove', onMove, { passive: true });
  document.addEventListener('pointerdown', onDown, { passive: true });
  document.addEventListener('pointerup', onUp, { passive: true });
  document.addEventListener('pointercancel', onUp, { passive: true });
  document.addEventListener('pointerout', onLeaveWindow, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('resize', invalidate, { passive: true });
  root.addEventListener('scroll', invalidate, { passive: true, capture: true });

  const mq = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const onMotionPref = (): void => {
    env.reduced = !!mq?.matches;
    if (env.reduced) {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      root.style.removeProperty('--gx'); root.style.removeProperty('--gy');
      for (const s of stations) {
        s.el.style.removeProperty('--tx'); s.el.style.removeProperty('--ty');
        s.el.style.removeProperty('--hov'); s.el.style.removeProperty('--press');
      }
    } else wake();
  };
  mq?.addEventListener?.('change', onMotionPref);

  wake();

  return {
    setPanelOpen(open: boolean): void {
      panelOpen = open;
      if (open) for (const s of stations) { if (s !== hoverState) { s.ttx = 0; s.tty = 0; } }
      wake();
    },
    dispose(): void {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      document.removeEventListener('pointerout', onLeaveWindow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', invalidate);
      root.removeEventListener('scroll', invalidate, { capture: true });
      mq?.removeEventListener?.('change', onMotionPref);
    },
  };
}
